// Forces a brand-new top-level navigation after an explicit sign-out (from
// ANY settings-modal sign-out path) or a genuine server logout, so the
// signed-out browser boots from a completely clean slate.
//
// A plain `window.location.reload()` is a SOFT reload: the browser may restore
// the just-authenticated page from the back/forward (bf) cache and revalidate
// the cached document rather than re-fetching it. It also leaves any service
// worker registered. This helper removes every one of those escape hatches AND
// guarantees a fully wiped slate, even if the caller's earlier wipe was raced
// out by a deadline (or never ran):
//
//   1. Run the canonical hard wipe (`hardWipeBrowserAuthStorage`): delete every
//      app IndexedDB database, drop every Cache Storage entry, unregister every
//      service worker, and clear localStorage / sessionStorage / cookies.
//      Timeboxed so a wedged step (e.g. a `blocked` IndexedDB delete held open
//      by another tab) can never delay the load-bearing navigation.
//   2. SYNCHRONOUSLY re-sweep localStorage / sessionStorage / cookies as the
//      VERY LAST step before navigating — with no `await` after it — so nothing
//      the still-live tab might re-write during the async window above (an
//      Avatar re-rendering and re-populating `ping.img.*`, a `com.ping.convname.*`
//      write) survives into the next load.
//   3. Navigate with `location.replace` to a one-shot cache-busting URL, which
//      forces the browser to re-fetch `index.html` from the network (defeats
//      the bfcache + cached document) and leaves the authenticated URL OUT of
//      session history (no Back into a dead session).
//
// This is now SELF-CONTAINED: callers may still run `clearAllLocalSessionState`
// + `hardWipeBrowserAuthStorage` first (to tear down the live SDK session and
// show a cleaning overlay), but `forceCleanReload` no longer DEPENDS on them
// having completed. Everything is best-effort: a failing step must never
// prevent the final navigation.

import { log } from '@core/log/log';

import {
  clearWebKeyValueStorageSync,
  hardWipeBrowserAuthStorage,
} from './hardWipeBrowserAuthStorage';

export async function forceCleanReload(reason: string): Promise<void> {
  if (typeof window === 'undefined') return;

  log.info({ event: 'session.forceCleanReload.begin', reason });

  // Full async teardown (IndexedDB + Cache Storage + service workers +
  // localStorage/sessionStorage/cookies), but NEVER let it block the navigation
  // for long: race it against a short deadline, then navigate no matter what.
  // This guarantees a complete wipe even on paths where the caller's wipe was
  // raced out by its own deadline — while keeping the reload prompt if a step
  // hangs (the reload is the load-bearing part).
  await Promise.race([
    hardWipeBrowserAuthStorage().catch(() => undefined),
    new Promise<void>((resolve) => {
      setTimeout(resolve, 1500);
    }),
  ]);

  // Final synchronous sweep, immediately before navigating, so a key the live
  // tab re-wrote during the async wipe above can't survive the reload.
  clearWebKeyValueStorageSync();

  log.info({ event: 'session.forceCleanReload.navigate', reason });

  // Force a fresh network fetch of the document via a one-shot cache-busting
  // param + `replace`: defeats the bf/document cache and keeps the dead
  // authenticated URL out of history. Falls back to a plain reload if URL
  // construction fails. The full reload also tears down the SDK Web Worker, so
  // a brand-new WASM instance (with no carried-over in-memory state) is created
  // on the next load.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_signedout', Date.now().toString(36));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}
